import { Card, HSpacer, Text, Toolbar, VSpacer } from '@/components/DesignSystem';
import { getUserDisplayName } from '@/utilities/Farmer';
import { ApiRetailer } from '@api/interfaces';
import { Box, CircularProgress, Pagination, Stack } from '@mui/material';
import React, { Fragment, useState } from 'react';
import { useInfiniteQuery, useQuery } from "react-query";
import { useTranslation } from 'react-i18next';
import { QueryKeys } from "@/constants/QueryKeys";
import { OrderTransactionsApi } from "@/utilities/api/OrderTransactionsApi";
import { OrderTransactionEndpoint } from "@api/endpoints/OrderTransactionEndpoint";
import { ApiOrderTransaction } from "@api/interfaces/ApiOrderTransaction";
import CheckCircle from "@mui/icons-material/CheckCircle";
import { useSearch } from "@/hooks/useSearch";
import { FilterSelections } from "@/components/DesignSystem/Toolbar/interfaces";
import { RetailerApi } from "@/utilities/api/RetailerApi";

const OrderTransactionList = ({ onError, selectedRetailer } : {
  onError: (error: string) => void,
  selectedRetailer: ApiRetailer,
}) => {
  const { t } = useTranslation();
  const { debouncedSearch, setSearch } = useSearch(3);
  const {
    debouncedSearch: debouncedFarmerSearch,
    search: farmerSearch,
    setSearch: setFarmerSearch,
  } = useSearch();
  const [filterSelections, setFilterSelections] =
    useState<FilterSelections | undefined>(() => new Map());

  const {
    data: farmersData,
    fetchNextPage: fetchNextFarmerPage,
    hasNextPage: hasNextFarmerPage,
    isFetching: areFarmersFetching,
  } = useInfiniteQuery(
    [QueryKeys.GET_RETAILER_ASSOCIATED_FARMERS, selectedRetailer.id, debouncedFarmerSearch],
    ({ pageParam = 0 }) => RetailerApi.getAssociatedFarmers(selectedRetailer.id, {
      hasOrderTransactions: true,
      page: pageParam,
      search: debouncedFarmerSearch || undefined,
    }),
    {
      getNextPageParam: (lastPage) => (
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined
      ),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
    },
  );

  const farmerId = Array.from(filterSelections?.get('farmerId') ?? [])[0];

  // Tie the page to the search and filter it was chosen for, so changing either starts at page 0
  // in the same render, without a reset effect that refetches the old search.
  const listKey = JSON.stringify([debouncedSearch, farmerId]);
  const [pageSelection, setPageSelection] = useState({ listKey, page: 0 });
  const page = pageSelection.listKey === listKey ? pageSelection.page : 0;

  const query : OrderTransactionEndpoint.List.Query = {
    farmerId,
    page,
    retailerId: selectedRetailer.id,
    search: debouncedSearch,
  };

  const { data: listOrdersResponse, isFetching: areOrdersFetching } = useQuery(
    [QueryKeys.GET_ORDERS, query],
    () => OrderTransactionsApi.list(query),
    { enabled: !!query.retailerId, keepPreviousData: true, onError },
  );
  const orders = listOrdersResponse?.data ?? [];
  const hasOrders = orders.length > 0;

  function formatDate (date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const currentYear = new Date().getFullYear();
    const targetYear = dateObj.getFullYear();
    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return targetYear !== currentYear ? `${formattedDate}, ${targetYear}` : formattedDate;
  }

  const farmerFilterOptions = (farmersData?.pages ?? [])
    .flatMap(({ data }) => data)
    .map((farmer) => ({ id: farmer.id, label: getUserDisplayName(farmer) }));

  return (
    <>
      <Stack alignItems="flex-end" direction="row" justifyContent="space-between">
        <Box sx={{ minWidth: '600px', maxWidth: '900px' }}>
          <Toolbar
            filterSelections={filterSelections}
            filters={[
              {
                id: 'farmerId',
                infiniteScrollOptions: {
                  fetchMoreItems: fetchNextFarmerPage,
                  hasMore: hasNextFarmerPage ?? false,
                  isLoading: areFarmersFetching,
                },
                label: 'Farmer',
                onSearch: setFarmerSearch,
                options: farmerFilterOptions,
                search: farmerSearch,
                selectionMethod: 'single-select',
              },
            ]}
            onChange={({ search, selections }) => {
              setSearch(search ?? '');
              setFilterSelections(selections);
            }}
            retainSelectionsOnFilterChange
            testID="orders-toolbar"
            totalItems={listOrdersResponse?.total}
            totalUnitLabel={{
              plural: `${selectedRetailer.name} ERP orders`,
              singular: `${selectedRetailer.name} ERP order`,
            }}
          />
        </Box>
      </Stack>
      <VSpacer size="3" />
      {areOrdersFetching && !hasOrders && (
        <Stack alignItems="center" py="24px">
          <CircularProgress data-testid="orders-loading" />
        </Stack>
      )}
      {!areOrdersFetching && !!listOrdersResponse && !hasOrders && (
        <Text category="body-medium" testID="orders-empty">
          {t($ => $.orders.emptyState)}
        </Text>
      )}
      <Box aria-busy={areOrdersFetching} data-testid="orders-list" position="relative">
        {areOrdersFetching && hasOrders && (
          <CircularProgress
            data-testid="orders-loading"
            sx={{
              left: '50%',
              position: 'absolute',
              top: '24px',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}
          />
        )}
        <Box sx={{ opacity: areOrdersFetching ? 0.5 : 1 }}>
          {orders.map((order: ApiOrderTransaction) => (
            <Fragment key={`order-card-${order.id}`}>
              <Card elevation={4} testID={`order-card-${order.id}`}>
                <Stack direction="row" justifyContent="space-between">
                  <Stack>
                    <Text category='overline'>
                      {order.farmer && getUserDisplayName(order.farmer)}
                    </Text>
                    <Text category='body-xlarge'>ERP Order #{order.externalId}</Text>
                    <Stack direction="row">
                      <Text category='body-large'>
                        Ordered on {formatDate(order.orderDate)}
                      </Text>
                      <HSpacer size="3"/>•<HSpacer size="3"/>
                      <Text category='body-large'>{new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                      }).format(order.totalCost)}</Text>
                    </Stack>
                  </Stack>
                  <Stack alignItems='center' direction="row">
                    <CheckCircle color='success' sx={{ width: '20px' }} />
                    <HSpacer size='3' />
                    <Text category='body-medium' color='success'>Rewards processed</Text>
                  </Stack>
                </Stack>
              </Card>
              <VSpacer size="5" />
            </Fragment>
          ))}
        </Box>
      </Box>
      <Stack alignItems="center">
        <VSpacer size="9" />
        <Pagination
            count={(listOrdersResponse?.lastPage ?? 0) + 1}
            onChange={
              (_, selectedPage) => setPageSelection({ listKey, page: selectedPage - 1 })
            }
            page={(listOrdersResponse?.page ?? 0) + 1}
        />
      </Stack>
    </>
  );
};

export default OrderTransactionList;
